/**
 * EmailField — email input with error display.
 *
 * Accessibility (TC-012 / WCAG 2.1 AA):
 *   - Input has an associated <label> via htmlFor.
 *   - Error message is linked via aria-describedby.
 *   - aria-invalid is set when error is present.
 *
 * Phase 1 design system changes:
 *  - focus:ring-blue-500 → focus:ring-blue-600 (WCAG AA 4.5:1 on white).
 */

import React from 'react';

interface EmailFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  autoComplete?: string;
}

export function EmailField({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  autoComplete = 'email',
}: EmailFieldProps) {
  const errorId = `${id}-error`;

  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        id={id}
        type="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        autoComplete={autoComplete}
        placeholder="you@example.com"
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? 'true' : undefined}
        className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary motion-safe:transition-colors ${
          error
            ? 'border-red-400 bg-red-50 focus:ring-red-400'
            : 'border-gray-300 bg-white'
        }`}
      />
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-sm text-red-600 flex items-start gap-1">
          <span aria-hidden="true">&#9888;</span>
          {error}
        </p>
      )}
    </div>
  );
}
